import usePermissions from '@/hooks/usePermissions';
import { getLandingPath } from '@/utils/permissionRegistry';
import { getEncryptedClientId } from '@/utils/helper';
import {
  checkKeepme,
  checkSuperOwner,
  checkUserKeepme,
} from '@/utils/checkAccess';
import { ReactNode } from 'react';
import NavigateDefault from './DefaultNaivgate';
import { Navigate } from 'react-router-dom';

interface SuperOwnerRouteProps {
  children: ReactNode;
}

/**
 * Platform-level route guard.
 *
 * Same reactivity requirement as AdminRoute: subscribe to the permission
 * slice so the decision is re-made once /roles/me resolves, and hold the
 * frame rather than redirecting while it is still in flight.
 */
/**
 * Where to send someone who cannot open the page they asked for: the first
 * screen their own permissions do reach. Falling back to '/' bounced them to
 * a landing page they often could not open either.
 */
const deniedPath = (): string => {
  const path = getLandingPath();
  if (!path) return '/';
  const client = getEncryptedClientId();
  return client ? `/${client}${path}` : path;
};

const SuperOwnerRoute = ({ children }: SuperOwnerRouteProps) => {
  const { isLoading } = usePermissions();

  if (isLoading) return null;

  const allowed = checkSuperOwner() || checkUserKeepme() || checkKeepme();
  return allowed ? <>{children}</> : <Navigate to={deniedPath()} replace />;
};

export default SuperOwnerRoute;
